import { createContext, useContext, useMemo } from 'react';

import { useThreadsList } from '../hooks/useThreadsList';

/**
 * Context for sharing thread-related state and actions across components
 * This centralizes thread data and ensures all components share the same state
 */
const ThreadListContext = createContext();

/**
 * Provider component that centralizes thread state management
 * Wraps useThreadsList() so all components share the same data and refetch functions
 * @param {object} props - Component props
 * @param {import('react').ReactNode} props.children - Child components to be wrapped
 * @returns {import('react').ReactElement} The provider component wrapping children
 */
export function ThreadListProvider({ children }) {
  // Centralize all thread-related state and actions
  const threadsState = useThreadsList();

  const enhancedState = useMemo(
    () => ({
      ...threadsState
      // addOptimisticThread
    }),
    [threadsState]
  );

  return <ThreadListContext.Provider value={enhancedState}>{children}</ThreadListContext.Provider>;
}

/**
 * Hook to access the threads context
 * @returns {object} Threads context value with threads, loading, error, refetch, etc.
 */
export function useThreadListContext() {
  const context = useContext(ThreadListContext);

  if (!context) {
    throw new Error('useThreadListContext must be used within a ThreadListProvider');
  }

  return context;
}

export default ThreadListContext;
